import { Text } from '@rockr/rock-ui'
import React, { useCallback, useEffect, useState } from 'react'
import { brlFormatter } from '../../shared/helpers/formatters'
import { OfferInput } from '../../shared/types/skus'
import { CustomIcon } from '../CustomIcon'
import { TableContainer, TableRow, TableHeader, TableData } from '../Table'

type Props = {
  skus: any[],
  offers: OfferInput[]
  calculateMultiplier: (index: number, quantity: number, duration: number) => number
  calculateTotal: (offer: OfferInput) => number
}

type StreamValues = {
  original: number
  discount: number
  discountPercentage: number
  total: number
}


export const ProductStreamsTable: React.FC<Props> = (props) => {
  const { offers, calculateMultiplier, calculateTotal } = props

  const [saasValues, setSaasValues] = useState<StreamValues>()
  const [servicesValues, setServicesValues] = useState<StreamValues>()
  const [marketplaceValues, setMarketplaceValues] = useState<StreamValues>()

  const calcDiscountPercentage = (original: number, total: number) => {
    const discount = (100 - ((total / original) * 100)) || 0
    return discount >= 0 ? discount : 0
  }

  const calcDiscount = (original: number, total: number) => original - total

  const calculateTotalAndOriginal = (payload: {
    multiplier: number
    accumulator: { total: number, original: number }
    current: OfferInput
  }): { original: number, total: number } => {
    const { multiplier, accumulator, current } = payload
    const original = accumulator.original + (Number(current.listPrice) * multiplier)
    const total = accumulator.total + calculateTotal(current)

    return { original, total }
  }

  const sumarizeStreamByCategory = useCallback((
    category: string
  ): { original: number, total: number } => {
    const productStreamResult = offers.reduce((previous, current) => {
      if(current.isChurn) {
        return previous
      }

      const { index, quantity, duration } = current
      // const multiplier = index * quantity * duration
      const multiplier = calculateMultiplier(index, quantity, duration)

      const bundleSkusTotal = current.skus?.reduce((previous, sku) => {
        if (sku.skuCode !== category || sku.value === 0) {
          return previous
        }
        // Setting the deal value of the bundle as the total/original works today
        // because all the bundle value is inside a single sku. Once this rule
        // changes, this chunk of code MUST be revisited
        return calculateTotalAndOriginal({
          multiplier,
          accumulator: previous,
          current
        })
      }, { original: 0, total: 0 })
      const bundleTotal = bundleSkusTotal?.total ?? 0
      const bundleOriginal = bundleSkusTotal?.original ?? 0

      if (bundleTotal > 0 || bundleOriginal > 0) {
        return {
          original:  previous.original + bundleOriginal,
          total: previous.total + bundleTotal
        }
      }

      if (current.skuCode === category) {
        return calculateTotalAndOriginal({
          multiplier,
          accumulator: previous,
          current
        })
      }

      return previous
    }, { original: 0, total: 0 })

    return productStreamResult
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offers])

  const resolveSaasValues = useCallback(() => {
    const { original, total } = sumarizeStreamByCategory('SFW')
    const discount = calcDiscount(original, total)
    const discountPercentage = calcDiscountPercentage(original, total)

    setSaasValues({
      original,
      discount,
      discountPercentage,
      total
    })
  }, [sumarizeStreamByCategory])

  const resolveMarketplaceValues = useCallback(() => {
    const { original, total } = sumarizeStreamByCategory('RCC')
    const discount = calcDiscount(original, total)
    const discountPercentage = calcDiscountPercentage(original, total)

    setMarketplaceValues({
      original,
      discount,
      discountPercentage,
      total
    })
  }, [sumarizeStreamByCategory])

  const resolveServicesValues = useCallback(() => {
    const { original, total } = sumarizeStreamByCategory('SVC')
    const discount = calcDiscount(original, total)
    const discountPercentage = calcDiscountPercentage(original, total)

    setServicesValues({
      original,
      discount,
      discountPercentage,
      total
    })
  }, [sumarizeStreamByCategory])

  useEffect(() => {
    resolveSaasValues()
    resolveMarketplaceValues()
    resolveServicesValues()
  }, [offers, resolveSaasValues, resolveMarketplaceValues, resolveServicesValues])

  const renderSaasTotals = () => {
    const original = saasValues?.original || 0
    const discount = saasValues?.discount || 0
    const discountPercentage = saasValues?.discountPercentage || 0
    const total = saasValues?.total || 0

    return (
      <TableRow>
        <TableData align='center'>
          <CustomIcon boxSize="45px" name='rock' />
        </TableData>
        <TableData align='left'>SaaS</TableData>
        <TableData>{brlFormatter.format(original / 100)}</TableData>
        <TableData>{brlFormatter.format(discount / 100)}</TableData>
        <TableData>{`${discountPercentage.toFixed(2)}%`}</TableData>
        <TableData>{brlFormatter.format(total / 100)}</TableData>
      </TableRow>
    )
  }

  const renderMarketplaceTotals = () => {
    const original = marketplaceValues?.original || 0
    const discount = marketplaceValues?.discount || 0
    const discountPercentage = marketplaceValues?.discountPercentage || 0
    const total = marketplaceValues?.total || 0

    return (
      <TableRow>
        <TableData align='center'>
          <CustomIcon boxSize="45px" name='tools' />
        </TableData>
        <TableData align='left'>Marketplace</TableData>
        <TableData>{brlFormatter.format(original / 100)}</TableData>
        <TableData>{brlFormatter.format(discount / 100)}</TableData>
        <TableData>{`${discountPercentage.toFixed(2)}%`}</TableData>
        <TableData>{brlFormatter.format(total / 100)}</TableData>
      </TableRow>
    )
  }

  const renderServiceTotals = () => {
    const original = servicesValues?.original || 0
    const discount = servicesValues?.discount || 0
    const discountPercentage = servicesValues?.discountPercentage || 0
    const total = servicesValues?.total || 0

    return (
      <TableRow>
        <TableData align='center'>
          <CustomIcon boxSize="45px" name='bag' />
        </TableData>
        <TableData align='left'>Professional Services</TableData>
        <TableData>{brlFormatter.format(original / 100)}</TableData>
        <TableData>{brlFormatter.format(discount / 100)}</TableData>
        <TableData>{`${discountPercentage.toFixed(2)}%`}</TableData>
        <TableData>{brlFormatter.format(total / 100)}</TableData>
      </TableRow>
    )
  }

  const renderFooter = () => {
    const totalOriginalValue = (
      saasValues?.original!
      + marketplaceValues?.original!
      + servicesValues?.original!
    )

    const totalDiscountValue = (
      saasValues?.discount!
      + marketplaceValues?.discount!
      + servicesValues?.discount!
    )

    const totalPaymentValue = (
      saasValues?.total!
      + marketplaceValues?.total!
      + servicesValues?.total!
    )

    const totalDiscountPercentageValue = calcDiscountPercentage(
      totalOriginalValue,
      totalPaymentValue
    )

    return (
      <TableRow>
        <TableData> </TableData>
        <TableData>
          <Text as="b" color="gray.500">Subtotal</Text>
        </TableData>
        <TableData>
          <Text as="b">{brlFormatter.format(totalOriginalValue / 100)}</Text>
        </TableData>
        <TableData>
          <Text as="b">{brlFormatter.format(totalDiscountValue / 100)}</Text></TableData>
        <TableData>
          <Text as="b">{`${totalDiscountPercentageValue.toFixed(2)}%`}</Text>
        </TableData>
        <TableData>
          <Text as="b">{brlFormatter.format(totalPaymentValue / 100)}</Text>
        </TableData>
      </TableRow>
    )
  }

  return (
    <TableContainer fullWidth>
      <TableRow>
        <TableHeader align='right' label="" />
        <TableHeader align='left' label="Business Unit" />
        <TableHeader label="Original Value" />
        <TableHeader label="Discount ($)" />
        <TableHeader label="Discount (%)" />
        <TableHeader label="Total Payment" />
      </TableRow>

      {renderSaasTotals()}

      {renderServiceTotals()}

      {renderMarketplaceTotals()}

      {renderFooter()}
    </TableContainer>
  )
}
